<template>
  <mf-card-container title="Detalhes dos segmentos" description="Aqui é possível visualizar mais detalhes a respeito de cada segmento da análise RFV.">
    <template v-slot:filters>
      <headers-data-table-settings
        :headers.sync="headers"
        :default-headers="getDefaultHeadersOptions()"
        headers-settings-name="rfv-segments-details-table-headers-configs"
      />
      <export-data :data-fetcher="getDataToExport" filename="rfv_detalhes_dos_segmentos" ghost />
    </template>
    <mf-loading class="pa-0 mt-4" :boolean-loader="loading" loader-type="chart">
      <v-data-table :headers="computedHeaders" :items="items" hide-default-footer>
        <template v-slot:header.percentageRevenue="{ header }">
          {{ header.text }}

          <mf-tooltip max-width="300" top variant="secondary">
            <template #activator="{ on }">
              <mf-icon dense color="cinza-d20" icon="info" />
            </template>
            Porcentagem do faturamento geral do período que é representado por esse grupo.
          </mf-tooltip>
        </template>

        <template v-slot:header.percentageTickets="{ header }">
          {{ header.text }}

          <mf-tooltip max-width="300" top variant="secondary">
            <template #activator="{ on }">
              <mf-icon dense color="cinza-d20" icon="info" />
            </template>
            Porcentagem dos cupons fiscais do período que é representado por esse grupo.
          </mf-tooltip>
        </template>

        <template v-slot:header.recency="{ header }">
          {{ header.text }}

          <mf-tooltip max-width="300" top variant="secondary">
            <template #activator="{ on }">
              <mf-icon dense color="cinza-d20" icon="info" />
            </template>
            Mediana em dias desde a última visita dos consumidores desse grupo.
          </mf-tooltip>
        </template>

        <template v-slot:header.frequency="{ header }">
          {{ header.text }}

          <mf-tooltip max-width="300" top variant="secondary">
            <template #activator="{ on }">
              <mf-icon dense color="cinza-d20" icon="info" />
            </template>
            Mediana da quantidade de vezes que um consumidor desse grupo visitou o mercado. Visita não é dia, é cupom.
          </mf-tooltip>
        </template>

        <template v-slot:item.segment="{ item }">
          <span>{{ item.segment }}</span>
        </template>
        <template v-slot:item.clients="{ item }">
          <span>{{ item.clients | localeString }}</span>
          <mf-quick-actions
            ref="quickAction"
            :quick-action-group-id="`rfv-clients-group-${getVariantName(item.segment)}`"
            :short-description="quickActionDescription(item.segment)"
            :long-description="quickActionDescription(item.segment)"
            :segmentation-group="segmentationGroup(getVariantName(item.segment))"
          />
        </template>
        <template v-slot:item.revenue="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>R$ {{ item.revenue | localeString(0) }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.monthlyAverageRevenuePerClient="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>R$ {{ item.monthlyAverageRevenuePerClient | localeString(2) }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.averageTicket="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>R$ {{ item.averageTicket | localeString(2) }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.recency="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>{{ setHifen.includes(item.segment) ? '-' : item.recency | localeString }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.frequency="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>{{ setHifen.includes(item.segment) ? '-' : item.frequency | localeString }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.percentageRevenue="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>{{ Math.floor(item.percentageRevenue) }}%</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.tickets="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>{{ item.tickets | localeString }}</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
        <template v-slot:item.percentageTickets="{ item }">
          <mf-tooltip top variant="secondary" :disabled="!setHifen.includes(item.segment)">
            <template #activator="{ on }">
              <span>{{ Math.floor(item.percentageTickets) }}%</span>
            </template>
            O segmento não possui compras no período analisado.
          </mf-tooltip>
        </template>
      </v-data-table>
    </mf-loading>
  </mf-card-container>
</template>

<script>
import { mapState } from 'vuex'
import { DateTime } from 'luxon'
import { cloneDeep } from 'lodash'
import { localeString } from '@/config/helpers.js'
import { criteriaGroup, isInSegmentCriteria, rfvClientsGroupCriteria } from '@/components/quick-actions/segmentationGroups.js'
import { rfvAnalysis } from '../../mixins/analise-rfv'

export default {
  name: 'RfvSegmentsDetailsTable',
  components: {
    HeadersDataTableSettings: () => import('@/components/HeadersDataTableSettings.vue'),
    ExportData: () => import('@/components/export-data/ExportData.vue'),
  },
  mixins: [rfvAnalysis],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    period: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    headers: [],
    setHifen: ['Nunca compraram', 'Perdidos'],
  }),
  computed: {
    ...mapState({ stateFilters: (state) => state.filters }),
    defaultHeaders() {
      let headers = [
        {
          text: 'Segmento',
          value: 'segment',
          width: '160px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Consumidores',
          value: 'clients',
          width: '160px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Faturamento',
          value: 'revenue',
          width: '175px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: '% do Faturamento',
          value: 'percentageRevenue',
          width: '175px',
          sortable: false,
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
        {
          text: 'Gasto médio mensal',
          value: 'monthlyAverageRevenuePerClient',
          width: '175px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Ticket médio',
          value: 'averageTicket',
          width: '140px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Cupons fiscais',
          value: 'tickets',
          width: '150px',
          sortable: false,
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
        {
          text: '% dos Cupons fiscais',
          value: 'percentageTickets',
          width: '200px',
          sortable: false,
          isActive: false,
          isRequired: false,
          isDefault: false,
        },
        {
          text: 'Recencia',
          value: 'recency',
          width: '130px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
        {
          text: 'Frequência',
          value: 'frequency',
          width: '130px',
          sortable: false,
          isActive: true,
          isRequired: false,
          isDefault: true,
        },
      ]
      return headers
    },
    computedHeaders() {
      const headers = this.headers.filter((header) => header.isActive)
      return headers.map((header) => {
        return {
          ...header,
          sortable: header.hasOwnProperty('sortable') ? header.sortable : true,
        }
      })
    },
    items() {
      let items = []
      if (!this.currentClientsMetrics) return []
      Object.entries(this.currentClientsMetrics).forEach((item) => {
        if (typeof item[1] === 'object') {
          const itemToAdd = {
            segment: this.getSegment(item[0]),
            clients: item[1].metrics.clients,
            revenue: item[1].metrics.revenue,
            monthlyAverageRevenuePerClient: item[1].metrics.monthlyAverageRevenuePerClient,
            averageTicket: item[1].metrics.averageTicket,
            recency: item[1].metrics.recency,
            frequency: item[1].metrics.frequency,
            percentageRevenue: item[1].metrics.percentageRevenue,
            tickets: item[1].metrics.tickets,
            percentageTickets: item[1].metrics.percentageTickets,
          }
          items.push(itemToAdd)
        }
      })
      return items
    },
  },
  mounted() {
    if (this.headers?.length <= 0) {
      this.headers = cloneDeep(this.defaultHeaders)
    }
  },
  methods: {
    getVariantName(value) {
      let name = ''
      switch (value) {
        case 'Em risco':
          name = 'atRisk'
          break
        case 'Clientes leais':
          name = 'loyals'
          break
        case 'Baixo valor':
          name = 'lowValue'
          break
        case 'Promissores':
          name = 'promising'
          break
        case 'Campeões':
          name = 'champions'
          break
        case 'Perdidos':
          name = 'losses'
          break
        case 'Nunca compraram':
          name = 'neverBought'
          break
      }
      return name
    },
    quickActionDescription(segment) {
      const formatDate = (date) => DateTime.fromFormat(date, 'yyyy-MM-dd').endOf('month').toFormat('MM/yyyy')
      return `${segment} entre ${formatDate(this.period.initialDate)} e ${formatDate(this.period.finalDate)}`
    },
    segmentationGroup(segment) {
      const criteria = [rfvClientsGroupCriteria(this.analyticsConfiguration._id, this.currentPeriod._id, [this.clientTypesToSegmentCluster[segment]])]
      if (this.stateFilters.segmentsObjects?.length) {
        this.stateFilters.segmentsObjects.forEach((segment) => {
          criteria.push(isInSegmentCriteria(segment))
        })
      }
      return criteriaGroup(criteria)
    },
    getDefaultHeadersOptions() {
      return cloneDeep(this.defaultHeaders)
    },
    getSegment(segment) {
      const segments = {
        champions: 'Campeões',
        loyals: 'Clientes leais',
        promising: 'Promissores',
        atRisk: 'Em risco',
        lowValue: 'Baixo valor',
        losses: 'Perdidos',
        neverBought: 'Nunca compraram',
      }

      return segments[segment]
    },
    getDataToExport() {
      const headers = this.getDefaultHeadersOptions().map((header) => header.text)
      const rows = this.items.map((item) => {
        return [
          item.segment,
          localeString(item.clients, 0),
          localeString(item.revenue, 2),
          Math.floor(item.percentageRevenue),
          localeString(item.monthlyAverageRevenuePerClient, 2),
          localeString(item.averageTicket, 2),
          localeString(item.tickets, 0),
          Math.floor(item.percentageTickets),
          localeString(item.recency, 0),
          localeString(item.frequency, 0),
        ]
      })
      const exportData = {
        headers,
        rows,
      }

      return exportData
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
</style>
